import { useMemo } from 'react';
import { styled } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image, type ImageProps } from '@hivebrite/design-system/Image';
import { useToggle } from '@hivebrite/react-hooks';

import { ImagePreviewModal } from 'hb-react/frontoffice/components/ImagePreviewModal';
import { VirusScanStatus } from 'hb-react/shared/hooks/useGlobalStorageV2/constants';
import { type StorageFileRecord } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

interface PreviewImage {
  default_url: string;
  file_name?: string;
  mime_type?: string;
  size?: number;
}

interface ImageWithPreviewProps extends Omit<ImageProps, 'src'> {
  enablePreview?: boolean;
  images?: Array<PreviewImage>;
  previewSrc?: string | null;
  src?: string;
}

const convertToStorageFileRecord = (
  image: PreviewImage,
  index: number,
): StorageFileRecord => ({
  id: index,
  default_url: image.default_url,
  file_name: image.file_name || `image-${index}`,
  file_url: image.default_url,
  name: image.file_name || `image-${index}`,
  mime_type: image.mime_type || 'image/jpeg',
  size: image.size || 0,
  virus_scan_status: VirusScanStatus.CLEAN,
});

export const ImageWithPreview = ({
  src,
  previewSrc,
  enablePreview = true,
  onClick,
  ...props
}: ImageWithPreviewProps) => {
  const { toggle, isOpen } = useToggle(false);

  const images = useMemo(
    () => [
      convertToStorageFileRecord({ default_url: previewSrc ?? src ?? '' }, 0),
    ],
    [previewSrc, src],
  );

  if (!src) {
    return null;
  }

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    onClick?.(event);

    if (enablePreview && src) {
      toggle();
    }
  };

  return (
    <Box overflow="hidden" display="inline-block">
      <ImageStyled
        src={src}
        onClick={handleImageClick}
        $enablePreview={enablePreview}
        {...props}
      />
      {enablePreview && src && isOpen && (
        <ImagePreviewModal
          images={images}
          isOpen
          handleCloseModal={toggle}
          fitToScreen
        />
      )}
    </Box>
  );
};

const ImageStyled = styled(Image)<{ $enablePreview?: boolean }>`
  cursor: ${({ $enablePreview }) => ($enablePreview ? 'pointer' : 'default')};

  &:hover {
    opacity: ${({ $enablePreview }) => ($enablePreview ? '0.9' : '1')};
    transition: opacity 0.2s ease-in-out;
  }
`;
